export enum SliceSpacingType {
    /**
     * SliceSpacing has not yet been determined
     */
    UNKNOWN,
    /**
     * There's not enough data to have slice spacing. Number of slices < 2.
     */
    NONE,
    /**
     * SliceSpacing is constant. Consecutive slices are always equally far apart.
     */
    CONSTANT,
    /**
     * SliceSpacing is constant but there are missing slices.\
     * So some slices are n*spacing apart, where n is some whole number.
     */
    MISSING_SLICES,
    /**
     * SliceSpacing is varying.
     */
    VARYING,
}

export class SliceSpacing {
    /**
     * The actual spacing between two consecutive slices in millimeters.\
     * If the {@link type} is one of {@link SliceSpacingType.UNKNOWN UNKNOWN}, {@link SliceSpacingType.NONE NONE} or {@link SliceSpacingType.VARYING VARYING}, this is undefined.\
     * For {@link type} {@link SliceSpacingType.CONSTANT CONSTANT} this is the value in between all the consecutive slices.\
     * For {@link type} {@link SliceSpacingType.MISSING_SLICES MISSING_SLICES} this is the value in between the slices that are closest to one another.
     */
    private _spacing?: number;

    /**
     * The {@link SliceSpacingType} of the {@link SliceSpacing}.
     */
    public readonly type: SliceSpacingType;

    /**
     * Constructor to create {@link SliceSpacing} of {@link SliceSpacing.type type} {@link SliceSpacingType.UNKNOWN UNKNOWN}, {@link SliceSpacingType.NONE NONE} or {@link SliceSpacingType.VARYING VARYING}.\
     * These types of {@link SliceSpacing} do not have a constant {@link _spacing spacing} value.
     * @param type The {@link SliceSpacing.type type} of the {@link SliceSpacing}. One of {@link SliceSpacingType.UNKNOWN UNKNOWN}, {@link SliceSpacingType.NONE NONE} or {@link SliceSpacingType.VARYING VARYING}.
     */
    public constructor(
        type:
            | SliceSpacingType.UNKNOWN
            | SliceSpacingType.NONE
            | SliceSpacingType.VARYING
    );

    /**
     * Constructor to create {@link SliceSpacing} of {@link SliceSpacing.type type} {@link SliceSpacingType.CONSTANT CONSTANT} or {@link SliceSpacingType.MISSING_SLICES MISSING_SLICES}.\
     * These {@link SliceSpacingType types} require additional {@link spacing}-parameter, which determines the spacing between the slices.\
     * For {@link SliceSpacingType.CONSTANT CONSTANT} the {@link spacing} between all consecutive slices is equal.\
     * For {@link SliceSpacingType.MISSING_SLICES MISSING_SLICES} the spacing between all consecutive slices is multiple of the smallest spacing. {@link spacing} should be the value of to the smallest spacing between two consecutive slices.
     * @param type The {@link SliceSpacing.type type} of the {@link SliceSpacing}. Either {@link SliceSpacingType.CONSTANT} or {@link SliceSpacingType.MISSING_SLICES}.
     * @param spacing Either the smallest spacing between two consecutive slices for type {@link SliceSpacingType.MISSING_SLICES MISSING_SLICES} or\
     * The spacing between all consecutive slices for type {@link SliceSpacingType.CONSTANT CONSTANT}.
     */
    public constructor(
        type: SliceSpacingType.CONSTANT | SliceSpacingType.MISSING_SLICES,
        spacing: number
    );

    public constructor(type: SliceSpacingType, spacing?: number) {
        this.type = type;
        this._spacing = spacing;
    }

    /**
     * Method to get the {@link _spacing spacing} of this {@link SliceSpacing}.\
     * This is 0 if the {@link type} is {@link SliceSpacingType.UNKNOWN UNKNOWN}, {@link SliceSpacingType.NONE NONE} or {@link SliceSpacingType.VARYING VARYING}.\
     * The smallest spacing between two consecutive slices if the {@link type} is {@link SliceSpacingType.MISSING_SLICES}.\
     * Or the spacing in between all consecutive slices if the {@link type} is {@link SliceSpacingType.CONSTANT}.
     */
    public get spacing(): number {
        return this._spacing || 0;
    }
}
